import QrScanner from "qr-scanner";
import { forwardRef, useCallback, useImperativeHandle, useRef } from "react";

export type ScannerQrScannerProps = {
	onResult: (result: { data: string }) => void;
	className?: string;
};

export type ScannerQrScannerRef = {
	switchCamera: () => Promise<void>;
	hasMultipleCameras: () => Promise<boolean>;
	toggleTorch: () => Promise<void>;
};

export const ScannerQrScanner = forwardRef<ScannerQrScannerRef, ScannerQrScannerProps>((props, forwardedRef) => {
	const { onResult, className } = props;
	const onResultRef = useRef(onResult);
	onResultRef.current = onResult;
	const scannerRef = useRef<QrScanner | null>(null);
	const currentCameraRef = useRef<"environment" | "user">("environment");
	const torchEnabledRef = useRef(false);

	useImperativeHandle(forwardedRef, () => ({
		switchCamera: async () => {
			if (!scannerRef.current) return;

			const newCamera = currentCameraRef.current === "environment" ? "user" : "environment";
			await scannerRef.current.setCamera(newCamera);
			currentCameraRef.current = newCamera;
		},
		hasMultipleCameras: async () => {
			const cameras = await QrScanner.listCameras(true);
			return cameras.length > 1;
		},
		toggleTorch: async () => {
			if (!scannerRef.current) return;

			const hasFlash = await scannerRef.current.hasFlash();
			if (!hasFlash) return;

			torchEnabledRef.current = !torchEnabledRef.current;
			await scannerRef.current.toggleFlash();
		},
	}));

	const ref = useCallback((el: HTMLVideoElement | null) => {
		if (!el) {
			if (scannerRef.current) {
				scannerRef.current.destroy();
				scannerRef.current = null;
			}
			return;
		}

		const scanner = new QrScanner(
			el,
			(result) => {
				onResultRef.current({ data: result.data });
			},
			{
				maxScansPerSecond: 10,
				preferredCamera: "environment",
				highlightScanRegion: true,
				highlightCodeOutline: true,
				returnDetailedScanResult: true,
			}
		);

		scannerRef.current = scanner;

		scanner.start();

		return () => {
			scanner.destroy();
			scannerRef.current = null;
		};
	}, []);

	return <video ref={ref} className={className} />;
});
